import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, ChevronDown, ChevronRight, FileText, Trash2, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const SubjectAssignmentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get('subject');
  const childId = searchParams.get('child');
  const { toast } = useToast();
  
  const [subjects, setSubjects] = useState([]);
  const [children, setChildren] = useState([]);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);

  useEffect(() => {
    loadData();
  }, [subjectId, childId]);

  const loadData = async () => {
    try {
      const [subjectsData, childrenData, plansData] = await Promise.all([
        api.getSubjects(),
        api.getChildren(),
        api.getLessonPlans(childId)
      ]);
      
      setSubjects(subjectsData);
      setChildren(childrenData);
      
      // Filter lesson plans for this subject only
      const filteredPlans = plansData.filter(p => p.subject_id === subjectId);
      setLessonPlans(filteredPlans);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load lesson plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await api.deleteAssignment(assignmentId);
      
      toast({
        title: 'Assignment Deleted',
        description: 'Assignment has been permanently deleted.'
      });

      // Reload data
      await loadData();
    } catch (error) {
      console.error('Failed to delete assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete assignment',
        variant: 'destructive'
      });
    }
  };

  const isOverdue = (assignment) => {
    if (assignment.status === 'graded' || assignment.status === 'submitted') {
      return false;
    }
    const dueDate = new Date(assignment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  const isDueToday = (assignment) => {
    if (assignment.status === 'graded' || assignment.status === 'submitted') {
      return false;
    }
    const dueDate = new Date(assignment.due_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate.getTime() === today.getTime();
  };

  const getStatusBadge = (assignment) => {
    const status = assignment.status;
    
    if (status === 'graded') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Completed
        </Badge>
      );
    } else if (status === 'submitted') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Clock className="w-3 h-3 mr-1" />
          Pending Review
        </Badge>
      );
    } else if (isOverdue(assignment)) {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-300">
          <AlertCircle className="w-3 h-3 mr-1" />
          Overdue
        </Badge>
      );
    } else if (isDueToday(assignment)) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Clock className="w-3 h-3 mr-1" />
          Due Today
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-300">
          <AlertCircle className="w-3 h-3 mr-1" />
          Assigned
        </Badge>
      );
    }
  };

  const sortAssignments = (assignments) => {
    // Sort: Overdue at top (most recent first), then assigned (not overdue), then completed at bottom
    return [...assignments].sort((a, b) => {
      const aOverdue = isOverdue(a);
      const bOverdue = isOverdue(b);
      const aCompleted = a.status === 'graded' || a.status === 'submitted';
      const bCompleted = b.status === 'graded' || b.status === 'submitted';

      // Overdue first
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;

      // Completed last
      if (aCompleted && !bCompleted) return 1;
      if (!aCompleted && bCompleted) return -1;

      // Within overdue category, sort by due date DESCENDING (most recent first)
      if (aOverdue && bOverdue) {
        return new Date(b.due_date) - new Date(a.due_date);
      }

      // Within other categories, sort by due date ascending
      return new Date(a.due_date) - new Date(b.due_date);
    });
  };

  const getPlanCounts = (assignments) => {
    let overdue = 0;
    let dueToday = 0;
    let toDo = 0;

    assignments.forEach(assignment => {
      if (isOverdue(assignment)) {
        overdue++;
      } else if (isDueToday(assignment)) {
        dueToday++;
      } else if (assignment.status === 'assigned') {
        toDo++;
      }
    });

    return { overdue, dueToday, toDo };
  };

  const subject = subjects.find(s => s.id === subjectId);
  const child = children.find(c => c.id === childId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="bg-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            {subject && (
              <div 
                className="inline-block px-4 py-2 rounded-lg text-xl font-bold mb-2"
                style={{ backgroundColor: `${subject.color}20`, color: subject.color }}
              >
                {subject.icon} {subject.name}
              </div>
            )}
            <h1 className="text-4xl font-bold text-slate-900">{child?.name}'s Lesson Plans</h1>
          </div>
        </div>

        {/* Lesson Plans List */}
        <div className="space-y-4">
          {lessonPlans.length === 0 ? (
            <Card className="p-12 text-center border-0 shadow-lg">
              <FileText className="w-24 h-24 text-slate-300 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">No Lesson Plans</h2>
              <p className="text-slate-600">
                No lesson plans found for {subject?.name}. Create one from the Lesson Plans page!
              </p>
            </Card>
          ) : (
            lessonPlans.map(plan => {
              const isExpanded = expandedPlan === plan.id;
              const assignments = plan.assignments || [];
              const sortedAssignments = sortAssignments(assignments);
              const counts = getPlanCounts(assignments);

              return (
                <Card key={plan.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-white overflow-hidden">
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.title}</h3>
                            <p className="text-sm text-slate-600 mb-2">{plan.description}</p>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(plan.start_date).toLocaleDateString()} - {new Date(plan.end_date).toLocaleDateString()}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            {/* Counters */}
                            <div className="flex items-center gap-3">
                              {counts.overdue > 0 && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">
                                  <AlertCircle className="w-3 h-3" />
                                  {counts.overdue} Overdue
                                </div>
                              )}
                              {counts.dueToday > 0 && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-semibold">
                                  <Clock className="w-3 h-3" />
                                  {counts.dueToday} Due Today
                                </div>
                              )}
                              {counts.toDo > 0 && (
                                <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                  <FileText className="w-3 h-3" />
                                  {counts.toDo} To Do
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {isExpanded ? (
                          <ChevronDown className="w-6 h-6 text-slate-400" />
                        ) : (
                          <ChevronRight className="w-6 h-6 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Assignments */}
                  {isExpanded && (
                    <div className="border-t bg-slate-50 p-6">
                      {assignments.length === 0 ? (
                        <p className="text-slate-500 text-center py-4">No assignments in this lesson plan yet.</p>
                      ) : (
                        <div className="space-y-3">
                          <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide mb-3">Assignments</h4>
                          {sortedAssignments.map(assignment => {
                            const overdueStatus = isOverdue(assignment);
                            const dueTodayStatus = isDueToday(assignment);
                            const borderColor = overdueStatus ? 'border-l-red-500' : 
                                              dueTodayStatus ? 'border-l-amber-500' :
                                              (assignment.status === 'graded' || assignment.status === 'submitted') ? 'border-l-green-500' : 
                                              'border-l-blue-500';
                            
                            return (
                              <div 
                                key={assignment.id} 
                                className={`flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border-l-4 ${borderColor}`}
                              >
                                <div className="flex-1">
                                  <h5 className="font-semibold text-slate-900">{assignment.title}</h5>
                                  <p className="text-sm text-slate-600 mb-2">{assignment.description}</p>
                                  <div className="flex items-center gap-3 text-xs text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      Due: {new Date(assignment.due_date).toLocaleDateString()}
                                    </span>
                                    <Badge variant="outline" className="text-xs">
                                      {assignment.points_possible} pts
                                    </Badge>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  {getStatusBadge(assignment)}
                                  {assignment.status === 'assigned' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteAssignment(assignment.id);
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectAssignmentsPage;
